import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Pagination from '../Paginations';
import { getReleases, getUserReleases } from '../../api/releases';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CircularProgress, Alert, Snackbar } from '@mui/material'

const Releases = () => {
    const uid = localStorage.getItem('uid')
    const [notification, setNotification] = useState({ open: false, message: '', type: 'success' })
    const [errorNotes, setErrorNotes] = useState('')
    const [editIndex, setEditIndex] = useState('')
    const closeRef = useRef()
    const [releases, setReleases] = useState([]);
    const [drafts, setDrafts] = useState([])
    const [localDrafts, setLocalDrafts] = useState([])
    
    // Load local drafts from localStorage
    useEffect(() => {
        const localDraftKey = `release_draft_${uid}`;
        const savedDrafts = JSON.parse(localStorage.getItem(localDraftKey) || '[]');
        setLocalDrafts(savedDrafts);
    }, [uid]);
    
    const { 
        data, 
        isLoading,
        error
    } = useQuery({
        queryKey: ['releases', uid],
        queryFn: () => getUserReleases(uid),
        select: (data) => {
            console.log(data, "data");
            const mappedRelease = data.data.releases.map(releaseIn => ({
                ...releaseIn,
                email: releaseIn.users_profile?.email || 'N/A',
                key: releaseIn?.users_profile?.user_id || '',
                index: releaseIn.id,
                status: releaseIn.status.toLowerCase().charAt(0).toUpperCase() + releaseIn.status.toLowerCase().slice(1),
                originalDate: releaseIn.original_date,
                digitalDate: releaseIn.digital_date,
                primaryGenre: releaseIn.primary_genre,
            }))
            return {
                releases: mappedRelease.sort((a, b) => new Date(b.originalDate) - new Date(a.originalDate)),
                drafts: data.drafts || []
            };
        }
    });
    useEffect(() => {
        if(data) {
            console.log(data)
            setReleases(data.releases);
            setDrafts(data.drafts);
        }
    }, [data, isLoading])
    const queryClient = useQueryClient();

    const deleteDraftMutation = useMutation({
        mutationFn: (draftID) => {
            const currentDrafts = [...(releases.drafts || [])]
            currentDrafts.splice(draftID, 1)
            // return update(child(ref(db, 'users'), uid), {
            //     drafts: currentDrafts
            // })
        },
        onSuccess: () => {
            setNotification({
                open: true,
                message: 'Draft deleted successfully',
                type: 'success'
            })
            queryClient.invalidateQueries(['releases', uid])
        },
        onError: (error) => {
            setNotification({
                open: true,
                message: 'Failed to delete draft',
                type: 'error'
            })
        }
    });

    // Function to delete a local draft
    const deleteLocalDraft = (index) => {
        const updatedDrafts = [...localDrafts];
        updatedDrafts.splice(index, 1);
        
        const localDraftKey = `release_draft_${uid}`;
        localStorage.setItem(localDraftKey, JSON.stringify(updatedDrafts));
        
        setLocalDrafts(updatedDrafts);
        setNotification({
            open: true,
            message: 'Draft deleted successfully',
            type: 'success'
        });
    };

    const statusClasses = {
        Distributed: 's-green',
        Processing: 's-blue',
        Takedown: 's-orange',
        Error: 's-red'
    }

    /////////// PAGINATION ///////////
    const itemsPerPage = 25;
    const [currentPage, setCurrentPage] = useState(1);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = releases.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(releases.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

  return (
    <div className='dash-section'>
        <h1 className='dash-title'>Releases</h1>
        <div className="row">
            <div className="col-12 mt-4">
                <div className="dash-card">
                    <div className="dash-card-heading">
                        <h2 className="dash-card-title">Releases</h2>
                        <Link to={'create'} className="dash-card-heading-btn" onClick={() => localStorage.removeItem('release')}>
                            <i className="fi fi-sr-plus"></i>
                            New Release
                        </Link>
                    </div>
                    <div className="table-responsive mt-3">
                        <table className="table dash-table">
                            <thead>
                                <tr>
                                    <td>ID</td>
                                    <td>Artwork</td>
                                    <td>Status</td>
                                    <td>Name</td>
                                    <td>Primary Artists</td>
                                    <td>Release Date</td>
                                    <td>UPC</td>
                                    <td>Action</td>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading && (
                                    <tr>
                                        <td colSpan="7" style={{ border: 'none' }}>
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '50px 0' }}>
                                                <CircularProgress />
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {error && (
                                    <tr>
                                        <td colSpan="7" style={{ border: 'none' }}>
                                            <Alert severity="error" sx={{ margin: '20px 0' }}>
                                                {error.message || 'Failed to load releases'}
                                            </Alert>
                                        </td>
                                    </tr>
                                )}

                                {!isLoading && !error && currentItems.length === 0 && (
                                    <tr>
                                        <td colSpan="7" style={{ border: 'none' }}>
                                            <div style={{ textAlign: 'center', padding: '50px 0' }}>
                                                No releases found
                                            </div>
                                        </td>
                                    </tr>
                                )}

                                {currentItems.map((release, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}</td>
                                        <td>
                                            <Link to={`/dashboard/releases/${i}`}>
                                                <img className='release-img' src={release.artwork} alt="" />
                                            </Link>
                                        </td>
                                        <td>
                                            <div 
                                            className={`release-status ${statusClasses[release.status]}`}
                                            >
                                                {release.status}
                                                {
                                                    release.status === "Error" && (
                                                        <button onClick={() => {setErrorNotes(release.statusNotes); setEditIndex(i)}} className='release-status-icon' data-bs-toggle="modal" data-bs-target="#notesModal">
                                                            View notes
                                                        </button>
                                                    )
                                                }
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-item">
                                                <Link to={`/dashboard/releases/${i}`} className='text-dark'>{release.title}</Link> <br />
                                                {/* <span>Release, Album, {release.label}</span> */}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-item">
                                                {
                                                    release.release_artist && release.release_artist.map((artist, i) => (
                                                        artist.role === 'Primary Artist' && (
                                                            <div key={i}>
                                                                {artist.artist.name}
                                                                {i < release.release_artist.filter(item => item.role === 'Primary Artist').length - 1 && ", "}
                                                            </div>
                                                        )
                                                    ))
                                                }
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-item">
                                                {release.originalDate}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-item">
                                                {release.upc}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="table-item d-flex">
                                                <Link 
                                                    className="table-action-btn a-blue me-1"
                                                    to={`edit/${release.index}`}
                                                >
                                                    <i className="fi fi-rr-pencil"></i>
                                                </Link>
                                            </div>
                                            </td>
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={currentPage} totalPages={totalPages} handlePageChange={handlePageChange} />
                </div>
            </div>
            <div className="col-12 mt-4">
                <div className="dash-card">
                    <div className="dash-card-heading">
                        <h2 className="dash-card-title">Drafts</h2>
                    </div>
                    
                    {/* Server Drafts Section */}
                    {drafts && drafts.length > 0 && (
                        <>
                            <h5 className="mt-3 mb-2">Server Drafts</h5>
                            <div className="row">
                                {
                                    drafts?.map((draft, i) => (
                                        <div className="col-md-6 mt-3" key={i}>
                                            <div className="draft-card">
                                                <h6>{draft.id}</h6>
                                                <div className="draft-card-actions">
                                                    <Link to={'/dashboard/releases/create'} onClick={() => localStorage.setItem('release', JSON.stringify(draft))} >
                                                        <i 
                                                            className="fi fi-rr-pencil table-action-btn a-blue"
                                                        ></i>
                                                    </Link>
                                                    <i 
                                                        className="fi fi-rr-trash table-action-btn a-red ms-2"
                                                        onClick={() => deleteDraftMutation.mutate(i)}
                                                    ></i>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        </>
                    )}
                    
                    {/* Local Drafts Section */}
                    {localDrafts && localDrafts.length > 0 && (
                        <>
                            <h5 className="mt-4 mb-2">Local Drafts</h5>
                            <div className="row">
                                {
                                    localDrafts.map((draft, i) => (
                                        <div className="col-md-6 mt-3" key={`local-${i}`}>
                                            <div className="draft-card">
                                                <div>
                                                    <h6>{draft.title || draft.id}</h6>
                                                    {draft.lastSaved && (
                                                        <small className="text-muted">
                                                            Last saved: {new Date(draft.lastSaved).toLocaleString()}
                                                        </small>
                                                    )}
                                                </div>
                                                <div className="draft-card-actions">
                                                    <Link to={'/dashboard/releases/create'} onClick={() => localStorage.setItem('release', JSON.stringify(draft))} >
                                                        <i 
                                                            className="fi fi-rr-pencil table-action-btn a-blue"
                                                        ></i>
                                                    </Link>
                                                    <i 
                                                        className="fi fi-rr-trash table-action-btn a-red ms-2"
                                                        onClick={() => deleteLocalDraft(i)}
                                                    ></i>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        </>
                    )}
                    
                    {(!drafts || drafts.length === 0) && (!localDrafts || localDrafts.length === 0) && (
                        <div className="text-center py-4">
                            <p>No drafts found. Start a new release and save it to create a draft.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>


        <div className="modal fade" id="notesModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="notesModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-body">
                        <div className="modal-top mb-4">
                            <h5>Error</h5>
                            <i ref={closeRef} className="fi fi-rr-cross" data-bs-dismiss="modal"></i>
                        </div>
                        <p>{errorNotes}</p>
                        <div className="d-flex justify-content-end">
                            <Link to={`/dashboard/releases/edit/${editIndex}`} onClick={() => closeRef.current.click()} className='dash-form-btn'>Edit Release</Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Add Snackbar for notifications */}
        <Snackbar
            open={notification.open}
            autoHideDuration={6000}
            onClose={() => setNotification({ ...notification, open: false })}
        >
            <Alert 
                onClose={() => setNotification({ ...notification, open: false })} 
                severity={notification.type}
                sx={{ width: '100%' }}
            >
                {notification.message}
            </Alert>
        </Snackbar>
    </div>
  )
}

export default Releases